import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    DIVIDENDS_ACCESS_COPY,
    type DividendsAccess,
} from '../../dividendsAccess';

type AccessNoticeProps = {
    /** Never `full` — the page renders the register in that case. */
    access: Exclude<DividendsAccess, { kind: 'full' }>;
};

/**
 * Why Profit & Dividends is unavailable to this session.
 *
 * Rendered **instead of** the register, and with no request having been made:
 * every endpoint here is OrgAdmin-with-a-password, so a BranchAdmin or a
 * PIN/kiosk session would collect nothing but 403s.
 *
 * The two cases read differently on purpose. A kiosk session has a remedy the
 * user owns: sign in with a password. A BranchAdmin does not, and is told so
 * plainly rather than being invited to retry.
 */
export const AccessNotice: React.FC<AccessNoticeProps> = ({ access }) => {
    const { t } = useTranslation('pages');
    const copy = DIVIDENDS_ACCESS_COPY[access.kind];

    return (
        <div
            className="flex flex-col items-center gap-2 rounded-2xl border border-separator bg-surface px-6 py-12 text-center"
            {...getNodeTestIdProp(`dividends-access-${access.kind}`)}
        >
            <Icon
                aria-hidden
                className="size-7 text-muted"
                icon="gravity-ui:lock"
            />
            <span className="text-sm font-medium text-foreground">
                {t(copy.titleKey)}
            </span>
            <span className="max-w-md text-sm text-muted">
                {t(copy.bodyKey)}
            </span>
        </div>
    );
};
